import { useTrackMarketing } from '@packages/shared/src/hooks/useTrackCampaign/useTrackCampaign';
import { useRef } from 'react';
import { ConditionalWrapper } from '@packages/shared/src/components/ConditionalWrapper/ConditionalWrapper';
import { Tooltip } from '@packages/modules/src/Tooltip/Tooltip';
import { cn } from '@packages/shared/src/utils/cn/cn';
import { Link } from '@packages/modules/src/Link/Link';
import { useGetTooltipContent } from '../../hooks/useGetTooltipContent';
import { buildLinkWithArguments } from '../../utils/buildLinkWithArguments';
import type { HeadlineLevel, HeadlineProps } from './types';
import { registerComponents } from '../ComponentRecognizer/componentRegistry';
import { ComponentTypes } from '../../../interfaces';
import { useAssignLinkTargetBasedOnBotStatus } from '../../hooks/useAssignLinkTargetBasedOnBotStatus';
import { SoftHyphenInjector } from '../SoftHyphenInjector';
import { classes } from '../../utils/classes';

export const CmsHeadline = ({ data, className }: HeadlineProps) => {
  const {
    field_margin_variant: marginVariant = 'none',
    field_link: fieldLink,
    field_link_title: linkTitle,
    field_link_target: linkTarget = '_self',
    behavior_paragraph_link_rel: linkRelRaw,
    field_text: headlineText,
    field_text_subheadline: subHeadlineText,
    field_no_h_element: noHeadlineTag,
    field_variation: styleVariation,
    field_text_align: textAlign,
    field_level: mainHeadlineLevel = 1,
    field_level_style: styleLevel,
    field_font: headlineFont,
    field_font_color: fontColor,
    field_tracking: tracking,
    promotion_item: tooltipDataPromotion,
    content_snippet_bundled: tooltipDataContentSnippet,
    behavior_paragraph_tooltip_activation: tooltipActivation,
    behavior_paragraph_tooltip_positioning: tooltipPositioning,
  } = data;

  const linkRel = linkRelRaw === '_none' ? undefined : linkRelRaw;

  const tooltipContent = useGetTooltipContent(tooltipDataPromotion, tooltipDataContentSnippet);

  // when styleVariation is set to 2, add a spacer
  const addSpacer = styleVariation === 2;

  // What the main headline looks like
  const mainHeadlineStyleLevel = styleLevel || mainHeadlineLevel;

  // Level of the sub headline. Should be 2 levels below main level (but minumum 6 of course)
  const subHeadlineLevel = Math.min(mainHeadlineLevel + 2, 6) as HeadlineLevel;

  // What the sub headline looks like. Should be 2 below main level (but minumum 6 of course)
  const subHeadlineStyleLevel = styleLevel
    ? (Math.min(mainHeadlineStyleLevel + 2, 6) as HeadlineLevel)
    : subHeadlineLevel;

  const MainHeadline = noHeadlineTag ? 'div' : (`h${mainHeadlineLevel}` as const);
  const SubHeadline = noHeadlineTag ? 'div' : (`h${subHeadlineLevel}` as const);

  const mainHeadline = (
    <MainHeadline
      className={classes(
        `text-size-h${mainHeadlineStyleLevel} hyphens-auto`,
        textAlign === 'center' && 'text-center',
        textAlign === 'left' && 'text-left',
        textAlign === 'right' && 'text-right',
        addSpacer &&
          "after:border-b-solid after:border-color-primary after:my-1 after:block after:w-[3.875rem] after:border-b-[0.125rem] after:content-['']",
        addSpacer && (textAlign === 'center' || textAlign === 'right')
          ? 'after:ml-auto'
          : 'after:ml-0',
        addSpacer && (textAlign === 'center' || textAlign === 'left')
          ? 'after:mr-auto'
          : 'after:mr-0',
        headlineFont,
      )}
      {...(noHeadlineTag && { 'aria-level': mainHeadlineLevel, role: 'heading' })}
      style={{ color: fontColor }}
    >
      {tooltipContent && !!tooltipActivation ? (
        <Tooltip variant="inline" content={tooltipContent} positioning={tooltipPositioning}>
          <SoftHyphenInjector text={headlineText} />
        </Tooltip>
      ) : (
        <SoftHyphenInjector text={headlineText} />
      )}
    </MainHeadline>
  );

  const subHeadline = (
    <SubHeadline
      className={classes(
        `text-size-h${subHeadlineStyleLevel} hyphens-auto`,
        textAlign === 'center' && 'text-center',
        textAlign === 'left' && 'text-left',
        textAlign === 'right' && 'text-right',
        headlineFont,
      )}
      {...(noHeadlineTag && { 'aria-level': subHeadlineLevel, role: 'heading' })}
      style={{ color: fontColor }}
    >
      {subHeadlineText && <SoftHyphenInjector text={subHeadlineText} />}
    </SubHeadline>
  );

  const headlineRef = useRef<HTMLDivElement>(null);
  const tmpLink = buildLinkWithArguments(fieldLink, data);
  const assignLinkTarget = useAssignLinkTargetBasedOnBotStatus();
  const finalLink = assignLinkTarget(tmpLink);
  const { trackClick } = useTrackMarketing(data, headlineRef, finalLink);
  return (
    <div
      ref={headlineRef}
      className={cn(
        className,
        'mx-0',
        marginVariant === 'big' && 'my-[1.5rem]',
        marginVariant === 'medium' && 'my-[0.75rem]',
        marginVariant === 'small' && 'my-[0.25rem]',
        marginVariant === 'none' && 'my-0',
      )}
      data-tracking={tracking}
    >
      <ConditionalWrapper
        condition={!!fieldLink}
        wrapper={(ch) => (
          <Link
            href={finalLink}
            {...(linkTarget && { target: linkTarget })}
            {...(linkTarget === '_blank' && { rel: 'noopener' })}
            rel={linkRel}
            title={linkTitle || ''}
            color="inherit" // no highlight color
            onClick={() => trackClick()}
          >
            {ch}
          </Link>
        )}
      >
        <>
          {mainHeadline}
          {subHeadlineText && subHeadline}
        </>
      </ConditionalWrapper>
    </div>
  );
};

// Needed to run this component in Storybook and Jest tests. Register the component in a global componentRegistry object
registerComponents({ [ComponentTypes.Headline]: CmsHeadline });
